import { Modal } from 'framework/ui/ui-builder';
import { FormFieldsData, ModalForm } from 'framework/form';
import StaffingAssignmentForm from '../StaffingAssignmentForm';
import UpdateStaffingAssignmentModalActions from './UpdateStaffingAssignmentModalActions';
import { AssignmentModalInitialData, StaffingAssignment } from 'domain/shared/staffing';
import { AssignmentType, useAssignmentService } from 'domain/shared/assignment';
import { useEmployeeService } from 'domain/shared/employee';
import { STAFFING_ASSIGNMENT_MESSAGE } from '../create/CreateStaffingAssignmentModal';

interface UpdateStaffingAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdateAfter?: (response: StaffingAssignment) => Promise<void>;
  onDeleteClick: () => void;
  isVirtual?: boolean;
  initialData: AssignmentModalInitialData;
}

const UpdateStaffingAssignmentModal = ({
  isOpen,
  onClose,
  onDeleteClick,
  onUpdateAfter,
  isVirtual,
  initialData,
}: UpdateStaffingAssignmentModalProps) => {
  const { getAssignmentById, update, getValidationSchema } = useAssignmentService();
  const { getById } = useEmployeeService();

  const getInitialData = async () => {
    const assignmentData = await getAssignmentById(String(initialData.id), true);
    const mergedData = {
      ...initialData,
      ...(assignmentData as Record<string, unknown>),
    };
    return {
      ...mergedData,
      assignmentType: AssignmentType.INDIVIDUAL_ASSIGNMENT,
    };
  };

  const onUpdate = async (values: FormFieldsData) => {
    const formValues = values as FormFieldsData<AssignmentModalInitialData>;
    const employee = formValues.employeeId ? await getById(formValues.employeeId) : null;
    const payload = {
      ...formValues,
      positionId: employee?.positionId ?? formValues.positionId,
      competencyLevel: employee?.specializationInfo?.competencyLevel ?? formValues.competencyLevel,
      technologyId: employee?.specializationInfo?.technologyId ?? formValues.technologyId,
    };
    return await update(payload.id, payload);
  };

  return (
    <Modal isOpen={isOpen} onToggle={onClose} title={`Update ${isVirtual ? 'planned ' : ''}assignment`}>
      <ModalForm
        getDataFunc={getInitialData}
        closeModal={onClose}
        modalOpen={isOpen}
        submitFormFunc={onUpdate}
        afterSubmit={{
          execute: onUpdateAfter,
          message: 'Assignment has been successfully updated.',
        }}
        getValidationSchemaFunc={getValidationSchema}
        isHideDefaultButtons
        messageContext={STAFFING_ASSIGNMENT_MESSAGE}
      >
        <StaffingAssignmentForm isVirtual={isVirtual} />
        <UpdateStaffingAssignmentModalActions closeModal={onClose} onDeleteAction={onDeleteClick} />
      </ModalForm>
    </Modal>
  );
};

export default UpdateStaffingAssignmentModal;
